// @ts-nocheck
import fieldDefinitionBuilder from "@store/Framework/Features/FieldDefinitionBuilder/fieldDefinitionBuilder";
import { FieldDefinitionBuilderOptions } from "@store/Framework/Features/FieldDefinitionBuilder/FieldDefinitionBuilderTypes";
import { responsiveCss } from "@store/Framework/Features/FieldDefinitionBuilder/Utilities/responsiveCss";
import { FoundationFieldDefinition } from "@store/Framework/Types/FoundationFieldDefinition";
import { BorderFieldOptions } from "@store/Overrides/Widgets/Basic/PageTitle";
import { lazyImageStyles } from "@store/Themes/common/mixins/lazyload";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CategoryContext } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import PageBreadcrumbs from "@insite/content-library/Components/PageBreadcrumbs";
import LazyImage from "@insite/mobius/LazyImage";
import { LazyImageProps } from "@insite/mobius/LazyImage/LazyImage";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CategoryNameStyles } from "@store/Overrides/Widgets/Category/CategoryName";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import getWidgetComponent from "@toolkit/Framework/Common/Utilities/getWidgetComponent";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { siteWidthStyles } from "@toolkit/Themes/common/mixins";
import parse from "html-react-parser";
import React, { useContext } from "react";
import { useSelector } from "react-redux";
import { css } from "styled-components";

const CategoryName = getWidgetComponent("Category/CategoryName");

const enum fields {
    hideBreadcrumbs = "hideBreadcrumbs",
    titleColor = "titleColor",
    hasBottomBorder = "hasBottomBorder",
    hasCustomOverlay = "hasCustomOverlay",
    customOverlayImage = "customOverlayImage",
    customOverlayPosition = "customOverlayPosition",
    customOverlayAligment = "customOverlayAligment",
    overlay = "overlay",
    colorOverlay = "colorOverlay",
    aligment = "aligment",
}

interface Props extends WidgetProps {
    fields: {
        [fields.hideBreadcrumbs]: boolean;
        [fields.titleColor]: string;
        [fields.hasBottomBorder]: boolean;
        [fields.hasCustomOverlay]: boolean;
        [fields.customOverlayImage]: string;
        [fields.customOverlayPosition]: number;
        [fields.customOverlayAligment]: "left" | "right";
        [fields.overlay]: boolean;
        [fields.colorOverlay]: string;
        [fields.aligment]: "flex-start" | "center" | "flexp-end";
    } & FieldDefinitionBuilderOptions<"titleType" | "titleStyle"> &
        BorderFieldOptions;
}

export interface CategoryDetailsHeaderStyles {
    wrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    overlayWrapper?: InjectableCss;
    image?: LazyImageProps;
    titleWrapper?: InjectableCss;
    title?: CategoryNameStyles;
    contentWrapper?: InjectableCss;
    textWrapper?: InjectableCss;
    customOverlayImage?: LazyImageProps;
}

export const categoryDetailsHeaderStyles: CategoryDetailsHeaderStyles = {
    wrapper: {
        css: css`
            background-color: ${getColor("common.background")}; /* Majestic Custom Code */
        `,
    },
    imageWrapper: {
        css: css`
            position: relative;
            display: flex;
            width: 100%;
            height: ${pxToRem(350)};
            border-bottom-color: ${getColor("secondary")};
            z-index: 0; /* Majestic Custom Code */

            ${breakpoint("sm", "down")} {
                height: 32vh;
            }
        `,
    },
    overlayWrapper: {
        css: css`
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            display: flex;
            align-items: center;
        `,
    },
    image: {
        css: css`
            position: relative;
            width: 100%;
            ${lazyImageStyles()}

            img {
                position: absolute;
                top: 50%;
                width: auto;
                min-width: 100%;
                min-height: 100%;
                max-width: none;
                transition: transform 1s cubic-bezier(0.215, 0.61, 0.355, 1);
                transform: translateY(-50%) scale(1);
            }
        `,
    },
    titleWrapper: {
        css: css`
            display: flex;
            align-items: center;
            width: 100%;
            z-index: 1; /* Majestic Custom Code */
            ${siteWidthStyles()}
        `,
    },
    contentWrapper: {
        css: css`
            ${siteWidthStyles()}
            padding-top: ${pxToRem(44)};
            padding-bottom: ${pxToRem(44)};
            text-align: center;

            ${breakpoint("sm")} {
                padding-top: ${pxToRem(28)};
                padding-bottom: ${pxToRem(28)};
            }

            body {
                background-color: inherit;
            }
        `,
    },
    textWrapper: {
        css: css`
            max-width: ${pxToRem(930)};
            margin: 0 auto;
        `,
    },
    customOverlayImage: {
        css: css`
            position: absolute;
            height: 50vh;

            img {
                height: 100%;
            }
        `,
    },
};

const styles = categoryDetailsHeaderStyles;

const CategoryDetailsHeader: React.FC<Props> = ({ fields }) => {
    const category = useContext(CategoryContext);
    const defaultImagePath = useSelector(
        (state: ApplicationState) =>
            getSettingsCollection(state).extensionCatalogSettings?.notFoundCategoryLargeImagePath,
    );

    if (!category) {
        return null;
    }

    const imagePath = category.largeImagePath || defaultImagePath;

    const imageWrapperStyles = (): InjectableCss => ({
        css: css`
            ${styles.imageWrapper?.css}
            ${fields.hasBottomBorder
                ? `
                border-bottom-width: ${pxToRem(8)};
                border-bottom-style: solid;
                `
                : ""}
        `,
    });

    const overlayWrapperStyles = {
        css: css`
            ${styles.overlayWrapper?.css || ""}

            ${responsiveCss(fields, field => {
                return css`
                    ${field.hasCustomOverlay &&
                    `
                    justify-content: ${field.customOverlayAligment === "right" ? "flex-end" : "flex-start"};
                `}
                    background-color: ${field.overlay && !field.hasCustomOverlay ? field.colorOverlay : "transparent"};
                `;
            })}
                overflow: hidden;
        `,
    };

    const customOverlayImageStyles: LazyImageProps = {
        ...styles.customOverlayImage,
        css: css`
            ${styles.customOverlayImage?.css}

            ${responsiveCss(fields, field => {
                return css`
                    ${field.customOverlayAligment === "right"
                        ? `transform: rotate(180deg) translateX(${field.customOverlayPosition}%);`
                        : `transform: translateX(-${field.customOverlayPosition}%);`}
                    ${field.hasCustomOverlay && !!field.customOverlayImage ? `display: inline-flex;` : `display: none;`}
                `;
            })}
        `,
    };

    const titleWrapperStyles = {
        css: css`
            ${styles.titleWrapper?.css}

            ${responsiveCss(
                fields,
                field => css`
                    justify-content: ${field.aligment};
                    /* Majestic Removed - z-index*/

                    > * {
                        color: ${field.titleColor || getColor("secondary.contrast")} !important;
                    }
                `,
            )}
        `,
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...imageWrapperStyles()}>
                <LazyImage src={imagePath} {...styles.image} />
                <StyledWrapper {...overlayWrapperStyles}>
                    <StyledWrapper {...titleWrapperStyles}>
                        <CategoryName titleType={fields.titleType} titleStyle={fields.titleStyle} />
                    </StyledWrapper>
                    {fields.hasCustomOverlay && fields.customOverlayImage && (
                        <LazyImage src={fields.customOverlayImage} {...customOverlayImageStyles} />
                    )}
                </StyledWrapper>
            </StyledWrapper>
            {!fields.hideBreadcrumbs ? <PageBreadcrumbs /> : null}
            {category.htmlContent ? (
                <StyledWrapper {...styles.contentWrapper}>
                    <StyledWrapper {...styles.textWrapper}>{parse(category.htmlContent, parserOptions)}</StyledWrapper>
                </StyledWrapper>
            ) : null}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule<FoundationFieldDefinition> = {
    component: CategoryDetailsHeader,
    definition: {
        group: "Categories",
        displayName: "Category Details Header",
        fieldDefinitions: [
            {
                name: fields.hideBreadcrumbs,
                displayName: "Hide categories breadcrumbs",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            ...fieldDefinitionBuilder(
                [
                    {
                        fieldType: "General",
                        name: fields.titleColor,
                        displayName: "Title Color",
                        editorTemplate: "ColorPickerField",
                        defaultValue: "",
                    },
                    {
                        name: fields.hasBottomBorder,
                        displayName: "Show Bottom Border?",
                        editorTemplate: "CheckboxField",
                        defaultValue: true,
                        fieldType: "General",
                    },
                    {
                        name: fields.hasCustomOverlay,
                        displayName: "Apply a Custom Overlay",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        fieldType: "General",
                    },
                    {
                        name: fields.customOverlayImage,
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        fieldType: "General",
                        isVisible: widget => widget.fields.hasCustomOverlay,
                    },
                    {
                        fieldType: "General",
                        name: fields.customOverlayPosition,
                        editorTemplate: "IntegerField",
                        defaultValue: 0,
                        tooltip: "The position is apllied in percentage, not in pixels",
                        isVisible: widget => widget.fields.hasCustomOverlay,
                    },
                    {
                        fieldType: "General",
                        name: fields.customOverlayAligment,
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Left", value: "left" },
                            { displayName: "Right", value: "right" },
                        ],
                        defaultValue: "left",
                        hideEmptyOption: false,
                        isVisible: widget => widget.fields.hasCustomOverlay,
                    },
                    {
                        name: fields.overlay,
                        displayName: "Add an Overlay",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        fieldType: "General",
                        isVisible: widget => !widget.fields.hasCustomOverlay,
                    },
                    {
                        fieldType: "General",
                        name: fields.colorOverlay,
                        displayName: "Image Color Overlay",
                        editorTemplate: "ColorPickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.overlay && !widget.fields.hasCustomOverlay,
                    },
                    {
                        fieldType: "General",
                        name: fields.aligment,
                        displayName: "Title Aligment",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Left", value: "flex-start" },
                            { displayName: "Center", value: "center" },
                            { displayName: "Right", value: "flex-end" },
                        ],
                        defaultValue: "center",
                        hideEmptyOption: true,
                    },
                ],
                {
                    typographyOptions: {
                        titleStyle: true,
                        titleType: true,
                    },
                },
            ),
        ],
    },
};

export default widgetModule;
